import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { Icon, IconArrowLeft, IconArrowRight } from '@flixbus/honeycomb-icons-react';
import { Button, Skeleton } from '@flixbus/honeycomb-react';

import ActivityChip from 'common/ActivityChip';
import ExtraActivityTimelineManagement from 'common/ExtraActivityTimelineManagement';
import { useExtraActivityHoverRetention } from 'common/ExtraActivityTimelineManagement/useExtraActivityHoverRetention';
import ShiftTrack from 'common/ShiftTrack';
import TimezoneSelector from 'common/TimezoneSelector';
import { formatNumberToHours, formatTime, isToday } from 'utils/datetime';

import MockDataToggle from './MockDataToggle';
import personalTimelineStore from './PersonalTimelineStore';
import { PERSONAL_TIMELINE_TEXT } from './text';
import {
	buildGroupedActiveItem,
	getActivityGroupChildClassNames,
	getActivityGroupChildFlexStyle,
	getShiftDensityChildCount,
	shouldRenderActiveItemClone,
} from './utils/activityGroupRendering';
import { addHoursToTwentyFour } from './utils/dayBoundaries';
import {
	getPeriodInPercentageLenghtOfDay,
	getPersentageOffsetFromStartOfDay,
} from './utils/positioning';

import styles from './styles.module.css';

const SHIFT_BOUNDER_SIDE_OFFSET_PX = 6;
const MIN_HOVER_WIDTH_PX = 160;
const MIN_EXTRA_HOVER_WIDTH_PX = 60;

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const clampToDay = (offset, width) => {
	const clampedOffset = clampPercent(offset);
	const clampedWidth = clampPercent(width);
	const maxWidth = 100 - clampedOffset;
	return {
		offset: clampedOffset,
		width: Math.max(0, Math.min(clampedWidth, maxWidth)),
	};
};

const PersonalTimeline = observer(() => {
	const { crewId } = useParams();
	const {
		homebaseTimezone,
		selectedTimezone,
		activities,
		isTimelineInCurrentWeek,
		timeline,
		isInitialized,
		isLoading,
		currentMoment,
		setTimelineToCurrentWeek,
		setTimelineToNextWeek,
		setTimelineToPreviousWeek,
		setSelectedTimezone,
		isError,
	} = personalTimelineStore;

	const [activeItem, setActiveItem] = useState(null);
	const [extraActivityActiveItemKey, setExtraActivityActiveItemKey] = useState(null);

	const handleSetActiveItem = useCallback((payload) => {
		setActiveItem(payload);
	}, []);

	const handleClearActiveItem = useCallback((expectedItemKey = null) => {
		setActiveItem((currentActiveItem) => {
			if (expectedItemKey && currentActiveItem?.key !== expectedItemKey) {
				return currentActiveItem;
			}

			return null;
		});
	}, []);

	const { handleExtraActivityInteractionChange, requestClearActiveItem, resetPendingClear } =
		useExtraActivityHoverRetention({
			activeItemId: activeItem?.key ?? null,
			onClearActiveItem: handleClearActiveItem,
		});

	const handleExtraActivityManagementInteractionChangeForItem = useCallback(
		(itemKey, isActive) => {
			setExtraActivityActiveItemKey((currentItemKey) => {
				if (isActive) return itemKey;
				if (currentItemKey === itemKey) return null;

				return currentItemKey;
			});

			if (activeItem?.key === itemKey) {
				handleExtraActivityInteractionChange(isActive);
			}
		},
		[activeItem?.key, handleExtraActivityInteractionChange]
	);

	useEffect(() => {
		personalTimelineStore.initializeData(crewId);
	}, [crewId]);

	if (!isInitialized || isError) return <div>{PERSONAL_TIMELINE_TEXT.loading}</div>;

	const renderTimeScale = () => {
		const hours = [];
		for (let i = 0; i < 25; i++) {
			hours.push(
				<div key={i} className={styles.hour}>
					{(i % 4 === 0 || i === 0) && (
						<div
							className={classNames(styles.hourNumber, {
								[styles.first]: i === 0,
								[styles.last]: i === 24,
							})}
						>
							{formatNumberToHours(i)}
						</div>
					)}
				</div>
			);
		}
		return hours;
	};

	const renderScaleLines = () => {
		const scaleLines = [];
		for (let i = 0; i < 5; i++) {
			scaleLines.push(<div key={`scaleline-${i}`} className={styles.scaleLine} />);
		}
		return scaleLines;
	};

	const renderDays = () => {
		return timeline?.map((day, index) => (
			<div
				className={classNames(styles.day, {
					[styles.bold]: isToday(day, selectedTimezone.value),
				})}
				key={day.format()}
			>
				<div className={styles.header}>
					{`${day.format('dddd, D MMM YYYY')} ${
						isToday(day, selectedTimezone.value) ? PERSONAL_TIMELINE_TEXT.todaySuffix : ''
					}`}
				</div>
				<div
					className={classNames(styles.activityContainer, {
						[styles.today]: isToday(day, selectedTimezone.value),
						[styles.loading]: isLoading,
					})}
				>
					{isLoading && <Skeleton Elem="div" height="lg" extraClasses={styles.skele} />}
					{!isLoading &&
						activities?.shifts?.[index]?.map((shift) => {
							const rawShiftOffset = getPersentageOffsetFromStartOfDay(
								shift.fromTimestamp,
								day,
								selectedTimezone.value
							);
							const rawShiftWidth = getPeriodInPercentageLenghtOfDay({
								fromTimestamp: shift.fromTimestamp,
								toTimestamp: shift.toTimestamp,
								currentDay: day,
								timeZone: selectedTimezone,
							});
							const { offset: shiftOffset, width: shiftWidth } = clampToDay(
								rawShiftOffset,
								rawShiftWidth
							);
							const autoCloseOffset = shift.autoCloseOnThisDay
								? clampPercent(
										getPersentageOffsetFromStartOfDay(
											shift.autoCloseTimestamp,
											day,
											selectedTimezone.value
										)
									)
								: null;

							const isShiftDimmed = Boolean(activeItem) && activeItem.shiftId !== shift.id;

							return (
								<React.Fragment key={shift.key}>
									<div
										className={classNames(styles.shiftTrack, {
											[styles.dimmed]: isShiftDimmed,
										})}
										style={{
											left: `${shiftOffset}%`,
											width: `${shiftWidth}%`,
										}}
									>
										<ShiftTrack
											timezone={selectedTimezone.value}
											shift={shift}
											isStartInTimeWindow={shift.isStartInTimeWindow !== false}
											isEndInTimeWindow={shift.isEndInTimeWindow !== false}
										/>
									</div>
									<div
										className={classNames(styles.shiftBounder, {
											[styles.dimmed]: isShiftDimmed,
											[styles.openStart]: shift.isStartInTimeWindow === false,
											[styles.openEnd]: shift.isEndInTimeWindow === false,
										})}
										style={{
											left: `calc(${shiftOffset}% - ${SHIFT_BOUNDER_SIDE_OFFSET_PX}px)`,
											width: `calc(${shiftWidth}% + ${SHIFT_BOUNDER_SIDE_OFFSET_PX * 2}px)`,
										}}
									/>
									{autoCloseOffset !== null && (
										<div
											className={classNames(styles.autoCloseMarker, {
												[styles.dimmed]: isShiftDimmed,
											})}
											style={{ left: `${autoCloseOffset}%` }}
										>
											<div className={styles.autoCloseLine} />
											<div className={styles.autoCloseLabel}>
												{PERSONAL_TIMELINE_TEXT.autoClose}
											</div>
										</div>
									)}
								</React.Fragment>
							);
						})}
					{!isLoading &&
						activities &&
						activities?.activities?.[index]?.map((activity) => {
							const rawOffset = getPersentageOffsetFromStartOfDay(
								activity.fromTimestamp,
								day,
								selectedTimezone.value
							);
							const toTimestampChecked =
								activity.fromTimestamp === activity.toTimestamp
									? addHoursToTwentyFour(activity.fromTimestamp)
									: activity.toTimestamp;

							const rawWidth = getPeriodInPercentageLenghtOfDay({
								fromTimestamp: activity.fromTimestamp,
								toTimestamp: toTimestampChecked,
								currentDay: day,
								timeZone: selectedTimezone,
							});
							const { offset, width } = clampToDay(rawOffset, rawWidth);
							const activityShiftId = activity?.activity?.shiftId ?? activity?.shiftId ?? null;
							const rootKey = activity.rootKey ?? activity.key;
							const activityChipRecord = { ...activity, crewMemberId: crewId };
							const isActivityStartInTimeWindow = activity.isStartInTimeWindow !== false;
							const isActivityEndInTimeWindow = activity.isEndInTimeWindow !== false;
							const isActiveSlice =
								activeItem?.dayIndex === index && activeItem?.key === activity.key;
							const isActiveRoot = activeItem?.rootKey === rootKey;
							const isDimmed = Boolean(activeItem) && !isActiveRoot;

							if (activity.isShiftGroup) {
								const groupClassName = classNames(styles.activityGroup, {
									[styles.activityGroupOpenStart]: activity.isStartInTimeWindow === false,
									[styles.activityGroupOpenEnd]: activity.isEndInTimeWindow === false,
									[styles.pseudoActivityGroup]: activity.isPseudoShiftGroup,
								});

								return (
									<div
										key={`${day.format()}-${activity.key}`}
										data-testid={`personal-shift-group-${activity.key}`}
										data-shift-density-count={getShiftDensityChildCount(activity.children)}
										className={groupClassName}
										style={{
											left: `${offset}%`,
											width: `${width}%`,
										}}
										onMouseEnter={resetPendingClear}
										onMouseLeave={requestClearActiveItem}
									>
										{activity.children.map((child) => {
											const childClassName = getActivityGroupChildClassNames(child, activeItem);
											const childChipRecord = { ...child, crewMemberId: crewId };
											const isChildStartInTimeWindow = child.isStartInTimeWindow !== false;
											const isChildEndInTimeWindow = child.isEndInTimeWindow !== false;
											const isChildExtraActivityActive = extraActivityActiveItemKey === child.key;

											return (
												<div
													key={child.key}
													data-testid={`personal-shift-child-${child.key}`}
													className={classNames(childClassName, {
														[styles.activityGroupChildExtraActivityActive]:
															isChildExtraActivityActive,
													})}
													style={getActivityGroupChildFlexStyle(child, activity.children)}
													onMouseEnter={() => {
														if (!child.isHoverable) return;

														handleSetActiveItem(
															buildGroupedActiveItem({
																childRecord: child,
																groupRecord: activity,
																dayIndex: index,
																crewMemberId: crewId,
															})
														);
													}}
												>
													<div className={styles.activityGroupChildContent}>
														<ActivityChip
															timezone={selectedTimezone.value}
															record={childChipRecord}
															isStartInTimeWindow={isChildStartInTimeWindow}
															isEndInTimeWindow={isChildEndInTimeWindow}
															extraActivityManagement={
																<ExtraActivityTimelineManagement
																	record={childChipRecord}
																	isStartInTimeWindow={isChildStartInTimeWindow}
																	isEndInTimeWindow={isChildEndInTimeWindow}
																/>
															}
															onExtraActivityManagementInteractionChange={(isActive) =>
																handleExtraActivityManagementInteractionChangeForItem(
																	child.key,
																	isActive
																)
															}
															suppressNativeTitle
														/>
													</div>
												</div>
											);
										})}
									</div>
								);
							}

							const activityClassName = classNames(styles.activity, {
								[styles.activityHidden]: isActiveSlice,
								[styles.dimmed]: !isActiveSlice && isDimmed,
								[styles.activityOpenStart]: activity.isStartInTimeWindow === false,
								[styles.activityOpenEnd]: activity.isEndInTimeWindow === false,
								[styles.activityExtraActivityActive]: extraActivityActiveItemKey === activity.key,
							});

							return (
								<div
									key={`${day.format()}-${activity.key}`}
									data-testid={`personal-activity-${activity.key}`}
									className={activityClassName}
									style={{
										left: `${offset}%`,
										width: `${width}%`,
									}}
									onMouseEnter={(event) => {
										if (!activity.isHoverable) return;

										const measuredWidthPx = event.currentTarget.getBoundingClientRect().width;

										handleSetActiveItem({
											dayIndex: index,
											key: activity.key,
											rootKey,
											shiftId: activityShiftId,
											record: activityChipRecord,
											leftPercent: offset,
											widthPercent: width,
											widthPx: measuredWidthPx,
											isExtraActivity: Boolean(activity.isExtraActivity),
											isStartInTimeWindow: isActivityStartInTimeWindow,
											isEndInTimeWindow: isActivityEndInTimeWindow,
											shouldRenderClone: true,
										});
									}}
								>
									<ActivityChip
										timezone={selectedTimezone.value}
										record={activityChipRecord}
										isStartInTimeWindow={isActivityStartInTimeWindow}
										isEndInTimeWindow={isActivityEndInTimeWindow}
										extraActivityManagement={
											<ExtraActivityTimelineManagement
												record={activityChipRecord}
												isStartInTimeWindow={isActivityStartInTimeWindow}
												isEndInTimeWindow={isActivityEndInTimeWindow}
											/>
										}
										onExtraActivityManagementInteractionChange={(isActive) =>
											handleExtraActivityManagementInteractionChangeForItem(activity.key, isActive)
										}
									/>
								</div>
							);
						})}
					{shouldRenderActiveItemClone(activeItem) &&
						activeItem.dayIndex === index &&
						(() => {
							const minHoverWidth = activeItem.isExtraActivity
								? MIN_EXTRA_HOVER_WIDTH_PX
								: MIN_HOVER_WIDTH_PX;
							const cloneWidthPx = Math.max(minHoverWidth, activeItem.widthPx || 0);

							return (
								<div
									data-testid="personal-active-item-hover-zone"
									className={styles.activeItemHoverZone}
									style={{
										left: `${activeItem.leftPercent}%`,
										width: `${cloneWidthPx}px`,
									}}
									onMouseEnter={resetPendingClear}
									onMouseLeave={requestClearActiveItem}
								>
									<div className={styles.activeItemClone} style={{ width: `${cloneWidthPx}px` }}>
										<ActivityChip
											timezone={selectedTimezone.value}
											record={activeItem.record}
											isStartInTimeWindow={activeItem.isStartInTimeWindow}
											isEndInTimeWindow={activeItem.isEndInTimeWindow}
											extraActivityManagement={
												<ExtraActivityTimelineManagement
													record={activeItem.record}
													isStartInTimeWindow={activeItem.isStartInTimeWindow}
													isEndInTimeWindow={activeItem.isEndInTimeWindow}
												/>
											}
											onExtraActivityManagementInteractionChange={(isActive) =>
												handleExtraActivityManagementInteractionChangeForItem(
													activeItem.key,
													isActive
												)
											}
										/>
									</div>
								</div>
							);
						})()}
					{isToday(day, selectedTimezone.value) && (
						<div
							className={styles.currentTime}
							style={{
								left: `${getPersentageOffsetFromStartOfDay(currentMoment, day, selectedTimezone.value)}%`,
							}}
						>
							<div className={styles.hoursMinutes}>
								{formatTime(currentMoment, { timezone: selectedTimezone.value })}
							</div>
						</div>
					)}
				</div>
			</div>
		));
	};

	return (
		<div>
			<MockDataToggle />
			<div className={styles.timelineHeader}>
				<div className={styles.controls}>
					<Button onClick={setTimelineToCurrentWeek} disabled={isTimelineInCurrentWeek}>
						{PERSONAL_TIMELINE_TEXT.today}
					</Button>
					<Icon
						InlineIcon={IconArrowLeft}
						extraClasses={styles.arrow}
						size={5}
						onClick={setTimelineToPreviousWeek}
					/>
					<Icon
						InlineIcon={IconArrowRight}
						extraClasses={styles.arrow}
						size={5}
						onClick={setTimelineToNextWeek}
					/>
					<div className={styles.fromToDates}>
						{`${timeline[0].format('ddd, D')} - ${timeline[timeline.length - 1].format(
							'ddd, D MMMM YYYY'
						)}`}
					</div>
				</div>
				<TimezoneSelector
					onSelection={setSelectedTimezone}
					additionalOptions={homebaseTimezone !== null ? [homebaseTimezone] : []}
					selected={selectedTimezone}
				/>
			</div>
			<div className={styles.dayscale}>{renderTimeScale()}</div>
			<div className={styles.container}>
				<div className={styles.background}>{renderScaleLines()}</div>
				{renderDays()}
			</div>
		</div>
	);
});
export default PersonalTimeline;
